import clsx from "clsx";
import React, { Fragment } from "react";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { CuratedProductsLinkSerializer } from "web/types/serializers";
import styles from "./curated-products-card.module.css";

interface CuratedProductsCardProps {
    links: CuratedProductsLinkSerializer[];
    isEmo?: boolean;
}

export default function CuratedProductsCard({
    links = [],
    isEmo,
}: CuratedProductsCardProps): JSX.Element {
    const { isMobileViewport } = useDomViewport();
    if (isEmo) {
        return (
            <View
                background="placeholder"
                className={clsx(styles.wrapper, styles.wrapperEmo)}
                padding={{ sm: "sm", lg: "lg" }}
            >
                <VStack spacing={isMobileViewport ? "md" : "lg"}>
                    <VStack spacing="xxs">
                        <Text color="secondaryBrand" textStyle="caption-2">
                            {gettext("feeds.curated_products.related_category")}
                        </Text>
                        <Heading as="h2" textStyle={{ sm: "title-3", lg: "title-2" }}>
                            {gettext("feed.curated_products.looking_for_something.title")}
                        </Heading>
                    </VStack>
                    <HStack spacing="xs">
                        {links.map(({ image, url, title }) => (
                            <Fragment key={url}>
                                <a
                                    href={url}
                                    onClick={(): void => {
                                        analytics.event("uf_infeed_cta", "click", title);
                                    }}
                                    data-testid={`image-${title}`}
                                >
                                    <View
                                        background="placeholder"
                                        padding="xxs"
                                        className={styles.imageContainerEmo}
                                    >
                                        <img
                                            className={styles.itemImage}
                                            src={image}
                                            alt={title}
                                            height={275}
                                            width={300}
                                        />
                                    </View>
                                    <View
                                        paddingX="sm"
                                        paddingY="xs"
                                        className={clsx(styles.wrapperEmo, styles.textWrapperEmo)}
                                    >
                                        <Text textStyle="body-2">{title}</Text>
                                    </View>
                                </a>
                            </Fragment>
                        ))}
                    </HStack>
                </VStack>
            </View>
        );
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <h4 className={styles.heading}>
                    {gettext("feed.curated_products.looking_for_something.title")}
                </h4>
            </div>
            <div className={styles.flex}>
                {links.map(({ image, url, title }, i) => (
                    <Fragment key={url}>
                        <div className={styles.item}>
                            <a
                                className={styles.itemLink}
                                href={url}
                                onClick={(): void => {
                                    analytics.event("uf_infeed_cta", "click", title);
                                }}
                                data-testid={`image-${title}`}
                            >
                                <img
                                    className={styles.itemImage}
                                    src={image}
                                    height={250}
                                    width={200}
                                />
                                <h5 className={styles.itemHeader}>{title}</h5>
                            </a>
                        </div>
                        {i % 2 === 0 ? <div className={styles.keyLine} /> : null}
                    </Fragment>
                ))}
            </div>
        </div>
    );
}
